import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  Paper,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  ReplySharp as ReplySharpIcon
} from '@mui/icons-material';

import { getListOperador } from '../../../controllers/crm/operadorController';
import { transferirLead } from '../../../controllers/crm/leadController';

const TransferirLead = ({ open, onClose, lead, idconta, onSuccess, setSnackbar }) => {
  const [corretores, setCorretores] = useState([]);
  const [corretoresLoading, setCorretoresLoading] = useState(false);
  const [corretoresFiltrados, setCorretoresFiltrados] = useState([]);
  const [buscaCorretor, setBuscaCorretor] = useState('');
  const [corretorSelecionado, setCorretorSelecionado] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);

  // Carregar corretores quando o dialog abre
  useEffect(() => {
    if (open && !corretores.length) {
      carregarCorretores();
    }
  }, [open]);

  // Filtrar corretores baseado na busca
  useEffect(() => {
    if (!buscaCorretor.trim()) {
      setCorretoresFiltrados(corretores);
    } else {
      const filtrados = corretores.filter(corretor =>
        corretor.nomeCompleto?.toLowerCase().includes(buscaCorretor.toLowerCase()) ||
        corretor.nome?.toLowerCase().includes(buscaCorretor.toLowerCase()) ||
        corretor.email?.toLowerCase().includes(buscaCorretor.toLowerCase())
      );
      setCorretoresFiltrados(filtrados);
    }
  }, [buscaCorretor, corretores]);

  // Função para carregar lista de corretores
  const carregarCorretores = async () => {
    setCorretoresLoading(true);
    try {
      const query = {
        where: {
          idconta: idconta,
          isactive: true
        },
        select: {
          idoperador: true,
          nome: true,
          telefone: true,
          status: true,
          isactive: true,
        },
        orderBy: [
          {
            nome: 'asc'
          }
        ]
      };

      const response = await getListOperador(query);

      if (response && response.data) {
        setCorretores(response.data);
        setCorretoresFiltrados(response.data);
      } else {
        setCorretores([]);
        setCorretoresFiltrados([]);
      }
    } catch (error) {
      console.error('Erro ao carregar corretores:', error);
      setSnackbar?.({ severity: 'error', message: 'Erro ao carregar lista de corretores' });
    } finally {
      setCorretoresLoading(false);
    }
  };

  // Função para selecionar corretor
  const handleCorretorSelect = (corretor) => {
    setCorretorSelecionado(corretor);
  };

  // Função para confirmar transferência
  const handleTransferConfirm = async () => {
    if (!corretorSelecionado) {
      setSnackbar?.({ severity: 'warning', message: 'Selecione um corretor para transferir o lead' });
      return;
    }

    setTransferLoading(true);
    try {
      const dados = {
        idlead: lead.idlead,
        idoperador: corretorSelecionado.idoperador,
        idconta: idconta,
        nome: corretorSelecionado.nome,
        version: lead.version
      };

      const response = await transferirLead(dados);

      if (process.env.REACT_APP_SHOWLOG === 'true') {
        console.log('Response da transferência:', response);
      }

      if (response.status !== 200) {
        setSnackbar?.({ severity: 'error', message: 'Erro ao transferir lead. Tente novamente.' });
        return;
      }

      setSnackbar?.({ severity: 'success', message: 'Lead transferido com sucesso!' });
      
      // Chamar callback de sucesso passando os dados atualizados
      if (onSuccess) {
        onSuccess({
          ...lead,
          corretor: corretorSelecionado.nome,
          motivo: 'Transferido',
          version: response.data.version
        });
      }

      handleClose();

    } catch (error) {
      console.error('Erro ao transferir lead:', error);
      setSnackbar?.({ severity: 'error', message: 'Erro ao transferir lead. Tente novamente.' });
    } finally {
      setTransferLoading(false);
    }
  };

  // Função para fechar o dialog e limpar estado
  const handleClose = () => {
    setBuscaCorretor('');
    setCorretorSelecionado(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Transferir Lead
        {lead && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Lead: {lead.nome || 'Nome não informado'} -
            Corretor atual: {lead.corretor || 'Não atribuído'}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione o novo corretor para este lead:
          </Typography>

          {/* Campo de busca */}
          <TextField
            fullWidth
            placeholder="Buscar corretor..."
            value={buscaCorretor}
            onChange={(e) => setBuscaCorretor(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Corretor selecionado */}
          {corretorSelecionado && (
            <Box sx={{ mb: 2 }}>
              <Chip
                avatar={<Avatar><PersonIcon /></Avatar>}
                label={`Selecionado: ${corretorSelecionado.nomeCompleto || corretorSelecionado.nome}`}
                color="primary"
                onDelete={() => setCorretorSelecionado(null)}
              />
            </Box>
          )}

          {/* Lista de corretores */}
          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
            {corretoresLoading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Carregando corretores...
                </Typography>
              </Box>
            ) : corretoresFiltrados.length > 0 ? (
              <List dense>
                {corretoresFiltrados.map((corretor, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton
                      selected={corretorSelecionado?.idoperador === corretor.idoperador}
                      onClick={() => handleCorretorSelect(corretor)}
                    >
                      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                        <PersonIcon />
                      </Avatar>
                      <ListItemText
                        primary={corretor.nomeCompleto || corretor.nome}
                        secondary={corretor.email}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {buscaCorretor ? 'Nenhum corretor encontrado' : 'Nenhum corretor disponível'}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={transferLoading}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleTransferConfirm}
          variant="contained"
          disabled={!corretorSelecionado || transferLoading}
          startIcon={transferLoading ? <CircularProgress size={16} /> : <ReplySharpIcon />}
        >
          {transferLoading ? 'Transferindo...' : 'Transferir Lead'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferirLead;